import React, { useState, useEffect } from 'react';
import { 
  Users, HardHat, Plus, Edit2, Trash2, Save, X, DollarSign,
  Clock, Briefcase, Award
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { toast } from 'sonner';

// Default labor types
const defaultLaborTypes = [
  // General Labor
  { id: 1, name: 'General Laborer', category: 'General', hourlyRate: 25, overtimeRate: 37.50, description: 'General construction labor' },
  { id: 2, name: 'Skilled Laborer', category: 'General', hourlyRate: 35, overtimeRate: 52.50, description: 'Experienced construction worker' },
  { id: 3, name: 'Foreman', category: 'General', hourlyRate: 55, overtimeRate: 82.50, description: 'Site foreman/supervisor' },
  { id: 4, name: 'Superintendent', category: 'General', hourlyRate: 75, overtimeRate: 112.50, description: 'Project superintendent' },
  
  // Trades
  { id: 5, name: 'Carpenter', category: 'Trades', hourlyRate: 45, overtimeRate: 67.50, description: 'Journeyman carpenter' },
  { id: 6, name: 'Carpenter Apprentice', category: 'Trades', hourlyRate: 28, overtimeRate: 42, description: 'Apprentice carpenter' },
  { id: 7, name: 'Electrician', category: 'Trades', hourlyRate: 55, overtimeRate: 82.50, description: 'Licensed electrician' },
  { id: 8, name: 'Plumber', category: 'Trades', hourlyRate: 55, overtimeRate: 82.50, description: 'Licensed plumber' },
  { id: 9, name: 'HVAC Technician', category: 'Trades', hourlyRate: 52, overtimeRate: 78, description: 'HVAC installer/technician' },
  { id: 10, name: 'Welder', category: 'Trades', hourlyRate: 48, overtimeRate: 72, description: 'Certified welder' },
  { id: 11, name: 'Mason', category: 'Trades', hourlyRate: 50, overtimeRate: 75, description: 'Brick/block mason' },
  { id: 12, name: 'Ironworker', category: 'Trades', hourlyRate: 52, overtimeRate: 78, description: 'Structural ironworker' },
  
  // Operators
  { id: 13, name: 'Equipment Operator', category: 'Operators', hourlyRate: 48, overtimeRate: 72, description: 'Heavy equipment operator' },
  { id: 14, name: 'Crane Operator', category: 'Operators', hourlyRate: 65, overtimeRate: 97.50, description: 'Certified crane operator' },
  { id: 15, name: 'Truck Driver', category: 'Operators', hourlyRate: 35, overtimeRate: 52.50, description: 'CDL truck driver' },
  
  // Specialty
  { id: 16, name: 'Painter', category: 'Specialty', hourlyRate: 38, overtimeRate: 57, description: 'Commercial painter' },
  { id: 17, name: 'Roofer', category: 'Specialty', hourlyRate: 42, overtimeRate: 63, description: 'Commercial roofer' },
  { id: 18, name: 'Concrete Finisher', category: 'Specialty', hourlyRate: 45, overtimeRate: 67.50, description: 'Concrete flatwork finisher' },
  { id: 19, name: 'Drywall Installer', category: 'Specialty', hourlyRate: 40, overtimeRate: 60, description: 'Drywall hanger' },
  { id: 20, name: 'Tile Setter', category: 'Specialty', hourlyRate: 48, overtimeRate: 72, description: 'Tile installation specialist' },
];

// Tab-specific labor types
const tabSpecificLaborTypes = {
  demo: [
    { id: 'd1', name: 'Demolition Laborer', category: 'Demo', hourlyRate: 28, overtimeRate: 42, description: 'General demolition worker' },
    { id: 'd2', name: 'Hazmat Removal Tech', category: 'Demo', hourlyRate: 45, overtimeRate: 67.50, description: 'Asbestos/lead removal certified' },
    { id: 'd3', name: 'Salvage Crew', category: 'Demo', hourlyRate: 30, overtimeRate: 45, description: 'Material salvage and sorting' },
    { id: 'd4', name: 'Cutting Torch Operator', category: 'Demo', hourlyRate: 42, overtimeRate: 63, description: 'Steel cutting specialist' },
    { id: 'd5', name: 'Concrete Breaker Operator', category: 'Demo', hourlyRate: 38, overtimeRate: 57, description: 'Jack hammer / breaker operator' },
    { id: 'd6', name: 'Demo Foreman', category: 'Demo', hourlyRate: 55, overtimeRate: 82.50, description: 'Demo crew supervisor' },
  ],
  excavation: [
    { id: 'e1', name: 'Excavator Operator', category: 'Excavation', hourlyRate: 52, overtimeRate: 78, description: 'Excavator operator' },
    { id: 'e2', name: 'Grading Operator', category: 'Excavation', hourlyRate: 50, overtimeRate: 75, description: 'Motor grader operator' },
    { id: 'e3', name: 'Dozer Operator', category: 'Excavation', hourlyRate: 48, overtimeRate: 72, description: 'Bulldozer operator' },
    { id: 'e4', name: 'Loader Operator', category: 'Excavation', hourlyRate: 45, overtimeRate: 67.50, description: 'Front-end loader operator' },
    { id: 'e5', name: 'Compaction Operator', category: 'Excavation', hourlyRate: 42, overtimeRate: 63, description: 'Roller/compactor operator' },
    { id: 'e6', name: 'Pipe Layer', category: 'Excavation', hourlyRate: 40, overtimeRate: 60, description: 'Underground pipe installer' },
    { id: 'e7', name: 'Grade Checker', category: 'Excavation', hourlyRate: 35, overtimeRate: 52.50, description: 'Survey/grade checking' },
  ],
  backfill: [
    { id: 'bf1', name: 'Backfill Laborer', category: 'Backfill', hourlyRate: 28, overtimeRate: 42, description: 'General backfill worker' },
    { id: 'bf2', name: 'Compaction Operator', category: 'Backfill', hourlyRate: 42, overtimeRate: 63, description: 'Roller/compactor operator' },
    { id: 'bf3', name: 'Loader Operator', category: 'Backfill', hourlyRate: 45, overtimeRate: 67.50, description: 'Front-end loader operator' },
    { id: 'bf4', name: 'Excavator Operator', category: 'Backfill', hourlyRate: 52, overtimeRate: 78, description: 'Excavator for material placement' },
    { id: 'bf5', name: 'Grade Checker', category: 'Backfill', hourlyRate: 35, overtimeRate: 52.50, description: 'Survey/grade checking' },
    { id: 'bf6', name: 'Backfill Foreman', category: 'Backfill', hourlyRate: 55, overtimeRate: 82.50, description: 'Backfill crew supervisor' },
  ],
  import: [
    { id: 'im1', name: 'Loader Operator', category: 'Import', hourlyRate: 45, overtimeRate: 67.50, description: 'Front-end loader operator' },
    { id: 'im2', name: 'Truck Driver', category: 'Import', hourlyRate: 35, overtimeRate: 52.50, description: 'Material delivery driver' },
    { id: 'im3', name: 'Spotter/Laborer', category: 'Import', hourlyRate: 28, overtimeRate: 42, description: 'Traffic/spotting laborer' },
    { id: 'im4', name: 'Grade Checker', category: 'Import', hourlyRate: 35, overtimeRate: 52.50, description: 'Survey/grade checking' },
    { id: 'im5', name: 'Import Foreman', category: 'Import', hourlyRate: 55, overtimeRate: 82.50, description: 'Import crew supervisor' },
  ],
  civil: [
    { id: 'c1', name: 'Concrete Finisher', category: 'Civil', hourlyRate: 45, overtimeRate: 67.50, description: 'Flatwork finishing' },
    { id: 'c2', name: 'Form Setter', category: 'Civil', hourlyRate: 42, overtimeRate: 63, description: 'Concrete form carpenter' },
    { id: 'c3', name: 'Rebar Tier', category: 'Civil', hourlyRate: 40, overtimeRate: 60, description: 'Reinforcing steel installer' },
    { id: 'c4', name: 'Paver Operator', category: 'Civil', hourlyRate: 55, overtimeRate: 82.50, description: 'Asphalt paver operator' },
    { id: 'c5', name: 'Paving Crew', category: 'Civil', hourlyRate: 35, overtimeRate: 52.50, description: 'Asphalt paving laborer' },
    { id: 'c6', name: 'Curb & Gutter Finisher', category: 'Civil', hourlyRate: 45, overtimeRate: 67.50, description: 'Curb/gutter specialist' },
    { id: 'c7', name: 'Striping Tech', category: 'Civil', hourlyRate: 38, overtimeRate: 57, description: 'Pavement marking' },
  ],
  environmental: [
    { id: 'v1', name: 'Erosion Control Tech', category: 'Environmental', hourlyRate: 32, overtimeRate: 48, description: 'BMP installation' },
    { id: 'v2', name: 'SWPPP Inspector', category: 'Environmental', hourlyRate: 45, overtimeRate: 67.50, description: 'Stormwater compliance' },
    { id: 'v3', name: 'Hydroseed Operator', category: 'Environmental', hourlyRate: 40, overtimeRate: 60, description: 'Hydroseeder equipment' },
    { id: 'v4', name: 'Landscape Laborer', category: 'Environmental', hourlyRate: 25, overtimeRate: 37.50, description: 'Planting/seeding crew' },
    { id: 'v5', name: 'Tree Crew', category: 'Environmental', hourlyRate: 35, overtimeRate: 52.50, description: 'Tree planting/removal' },
    { id: 'v6', name: 'Irrigation Tech', category: 'Environmental', hourlyRate: 38, overtimeRate: 57, description: 'Irrigation installation' },
  ],
};

// Tab-specific equipment types
const tabSpecificEquipmentTypes = {
  demo: [
    { id: 'd1', name: 'Excavator w/Thumb', category: 'Demo', dailyRate: 750, weeklyRate: 3000, monthlyRate: 8500, description: 'Excavator with demolition thumb' },
    { id: 'd2', name: 'Excavator w/Breaker', category: 'Demo', dailyRate: 950, weeklyRate: 3800, monthlyRate: 10500, description: 'Excavator with hydraulic breaker' },
    { id: 'd3', name: 'Skid Steer w/Grapple', category: 'Demo', dailyRate: 375, weeklyRate: 1500, monthlyRate: 3800, description: 'Skid steer with grapple bucket' },
    { id: 'd4', name: 'Walk-Behind Saw', category: 'Demo', dailyRate: 150, weeklyRate: 600, monthlyRate: 1500, description: 'Concrete saw for cutting' },
    { id: 'd5', name: 'Jack Hammer', category: 'Demo', dailyRate: 85, weeklyRate: 340, monthlyRate: 850, description: 'Electric/pneumatic breaker' },
    { id: 'd6', name: 'Dumpster (20yd)', category: 'Demo', dailyRate: 150, weeklyRate: 450, monthlyRate: 900, description: 'Roll-off dumpster' },
    { id: 'd7', name: 'Dumpster (40yd)', category: 'Demo', dailyRate: 200, weeklyRate: 600, monthlyRate: 1200, description: 'Large roll-off dumpster' },
  ],
  excavation: [
    { id: 'e1', name: 'Excavator (Mini)', category: 'Excavation', dailyRate: 350, weeklyRate: 1400, monthlyRate: 3500, description: '3-5 ton mini excavator' },
    { id: 'e2', name: 'Excavator (Standard)', category: 'Excavation', dailyRate: 650, weeklyRate: 2600, monthlyRate: 7500, description: '15-20 ton excavator' },
    { id: 'e3', name: 'Excavator (Large)', category: 'Excavation', dailyRate: 1200, weeklyRate: 4800, monthlyRate: 14000, description: '30+ ton excavator' },
    { id: 'e4', name: 'Backhoe', category: 'Excavation', dailyRate: 450, weeklyRate: 1800, monthlyRate: 4500, description: 'Standard backhoe loader' },
    { id: 'e5', name: 'Bulldozer (D6)', category: 'Excavation', dailyRate: 850, weeklyRate: 3400, monthlyRate: 9500, description: 'D6 class bulldozer' },
    { id: 'e6', name: 'Motor Grader', category: 'Excavation', dailyRate: 950, weeklyRate: 3800, monthlyRate: 10500, description: '12ft blade grader' },
    { id: 'e7', name: 'Vibratory Roller', category: 'Excavation', dailyRate: 350, weeklyRate: 1400, monthlyRate: 3500, description: 'Smooth drum roller' },
    { id: 'e8', name: 'Sheep\'s Foot Roller', category: 'Excavation', dailyRate: 400, weeklyRate: 1600, monthlyRate: 4000, description: 'Padfoot compactor' },
    { id: 'e9', name: 'Water Truck', category: 'Excavation', dailyRate: 400, weeklyRate: 1600, monthlyRate: 4000, description: '2000-4000 gal truck' },
  ],
  backfill: [
    { id: 'bf1', name: 'Excavator (Standard)', category: 'Backfill', dailyRate: 650, weeklyRate: 2600, monthlyRate: 7500, description: '15-20 ton excavator for placement' },
    { id: 'bf2', name: 'Loader (Wheel)', category: 'Backfill', dailyRate: 450, weeklyRate: 1800, monthlyRate: 4500, description: 'Wheel loader for spreading' },
    { id: 'bf3', name: 'Skid Steer', category: 'Backfill', dailyRate: 275, weeklyRate: 1100, monthlyRate: 2800, description: 'Skid steer for tight areas' },
    { id: 'bf4', name: 'Vibratory Roller', category: 'Backfill', dailyRate: 350, weeklyRate: 1400, monthlyRate: 3500, description: 'Smooth drum roller' },
    { id: 'bf5', name: 'Sheep\'s Foot Roller', category: 'Backfill', dailyRate: 400, weeklyRate: 1600, monthlyRate: 4000, description: 'Padfoot compactor' },
    { id: 'bf6', name: 'Plate Compactor', category: 'Backfill', dailyRate: 75, weeklyRate: 300, monthlyRate: 750, description: 'Walk-behind plate compactor' },
    { id: 'bf7', name: 'Jumping Jack', category: 'Backfill', dailyRate: 65, weeklyRate: 260, monthlyRate: 650, description: 'Rammer compactor for trenches' },
    { id: 'bf8', name: 'Water Truck', category: 'Backfill', dailyRate: 400, weeklyRate: 1600, monthlyRate: 4000, description: 'For moisture conditioning' },
  ],
  import: [
    { id: 'im1', name: 'Dump Truck (10-wheel)', category: 'Import', dailyRate: 650, weeklyRate: 2600, monthlyRate: 7500, description: '14-16 cy dump truck' },
    { id: 'im2', name: 'Dump Truck (18-wheel)', category: 'Import', dailyRate: 850, weeklyRate: 3400, monthlyRate: 9500, description: 'Semi dump trailer' },
    { id: 'im3', name: 'Loader (Wheel)', category: 'Import', dailyRate: 450, weeklyRate: 1800, monthlyRate: 4500, description: 'Wheel loader for stockpile' },
    { id: 'im4', name: 'Belt Loader', category: 'Import', dailyRate: 550, weeklyRate: 2200, monthlyRate: 5500, description: 'Conveyor belt loader' },
    { id: 'im5', name: 'Water Truck', category: 'Import', dailyRate: 400, weeklyRate: 1600, monthlyRate: 4000, description: 'Dust control/moisture' },
  ],
  civil: [
    { id: 'c1', name: 'Concrete Mixer Truck', category: 'Civil', dailyRate: 500, weeklyRate: 2000, monthlyRate: 5000, description: '8-10 cy mixer truck' },
    { id: 'c2', name: 'Concrete Pump', category: 'Civil', dailyRate: 1200, weeklyRate: 4800, monthlyRate: 14000, description: '32m boom pump' },
    { id: 'c3', name: 'Power Trowel', category: 'Civil', dailyRate: 125, weeklyRate: 500, monthlyRate: 1250, description: 'Ride-on power trowel' },
    { id: 'c4', name: 'Asphalt Paver', category: 'Civil', dailyRate: 1500, weeklyRate: 6000, monthlyRate: 18000, description: 'Track paver' },
    { id: 'c5', name: 'Asphalt Roller', category: 'Civil', dailyRate: 450, weeklyRate: 1800, monthlyRate: 4500, description: 'Double drum roller' },
    { id: 'c6', name: 'Curb Machine', category: 'Civil', dailyRate: 650, weeklyRate: 2600, monthlyRate: 7500, description: 'Slip-form curb machine' },
    { id: 'c7', name: 'Concrete Saw', category: 'Civil', dailyRate: 150, weeklyRate: 600, monthlyRate: 1500, description: 'Walk-behind saw' },
  ],
  environmental: [
    { id: 'v1', name: 'Hydroseeder', category: 'Environmental', dailyRate: 650, weeklyRate: 2600, monthlyRate: 7500, description: '1000 gal hydroseeder' },
    { id: 'v2', name: 'Straw Blower', category: 'Environmental', dailyRate: 350, weeklyRate: 1400, monthlyRate: 3500, description: 'Straw/mulch blower' },
    { id: 'v3', name: 'Trencher', category: 'Environmental', dailyRate: 275, weeklyRate: 1100, monthlyRate: 2800, description: 'Walk-behind trencher' },
    { id: 'v4', name: 'Skid Steer', category: 'Environmental', dailyRate: 275, weeklyRate: 1100, monthlyRate: 2800, description: 'With grading bucket' },
    { id: 'v5', name: 'Silt Fence Installer', category: 'Environmental', dailyRate: 200, weeklyRate: 800, monthlyRate: 2000, description: 'Mechanical installer' },
    { id: 'v6', name: 'Tree Spade', category: 'Environmental', dailyRate: 450, weeklyRate: 1800, monthlyRate: 4500, description: 'Tree transplanter' },
  ],
};

const LaborRates = ({ laborTypes, setLaborTypes }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLabor, setEditingLabor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    hourlyRate: '',
    overtimeRate: '',
    description: ''
  });

  // Initialize with defaults if empty
  useEffect(() => {
    if (!laborTypes || laborTypes.length === 0) {
      setLaborTypes(defaultLaborTypes);
    }
  }, [laborTypes, setLaborTypes]);

  const categories = ['all', ...new Set((laborTypes || []).map(l => l.category))];

  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredLabor = (laborTypes || []).filter(l => 
    selectedCategory === 'all' || l.category === selectedCategory
  );

  const groupedLabor = filteredLabor.reduce((acc, l) => {
    if (!acc[l.category]) acc[l.category] = [];
    acc[l.category].push(l);
    return acc;
  }, {});

  const resetForm = () => {
    setFormData({ name: '', category: '', hourlyRate: '', overtimeRate: '', description: '' });
    setShowAddForm(false);
    setEditingLabor(null);
  };

  const handleSave = () => {
    if (!formData.name || !formData.category || !formData.hourlyRate) {
      toast.error('Please fill in required fields');
      return;
    }

    const hourlyRate = parseFloat(formData.hourlyRate);
    const laborData = {
      ...formData,
      hourlyRate,
      overtimeRate: parseFloat(formData.overtimeRate) || hourlyRate * 1.5
    };

    if (editingLabor) {
      setLaborTypes(laborTypes.map(l => 
        l.id === editingLabor.id ? { ...l, ...laborData } : l
      ));
      toast.success('Labor rate updated');
    } else {
      setLaborTypes([...laborTypes, { ...laborData, id: Date.now() }]);
      toast.success('Labor type added');
    }
    resetForm();
  };

  const handleEdit = (labor) => {
    setFormData({
      name: labor.name,
      category: labor.category,
      hourlyRate: labor.hourlyRate.toString(),
      overtimeRate: labor.overtimeRate?.toString() || '',
      description: labor.description || ''
    });
    setEditingLabor(labor);
    setShowAddForm(true);
  };

  const handleDelete = (id) => {
    setLaborTypes(laborTypes.filter(l => l.id !== id));
    toast.success('Labor type deleted');
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Labor Rates
          </CardTitle>
          <Button size="sm" onClick={() => setShowAddForm(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
        >
          {categories.map(cat => (
            <option key={cat} value={cat}>
              {cat === 'all' ? 'All Categories' : cat}
            </option>
          ))}
        </select>

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="p-4 bg-muted/30 rounded-lg space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name *</Label>
                <Input
                  placeholder="Labor type"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Category *</Label>
                <Input
                  placeholder="e.g., Trades"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="h-8 text-sm"
                  list="laborCategories"
                />
                <datalist id="laborCategories">
                  {categories.filter(c => c !== 'all').map(cat => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Hourly Rate ($) *</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={formData.hourlyRate}
                  onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                  className="h-8 text-sm"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Overtime Rate ($)</Label>
                <Input
                  type="number"
                  placeholder="Auto: 1.5x"
                  value={formData.overtimeRate}
                  onChange={(e) => setFormData({ ...formData, overtimeRate: e.target.value })}
                  className="h-8 text-sm"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Description</Label>
              <Input
                placeholder="Brief description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="h-8 text-sm"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={resetForm}>
                <X className="w-3 h-3 mr-1" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave}>
                <Save className="w-3 h-3 mr-1" />
                Save
              </Button>
            </div>
          </div>
        )}

        {/* Labor List */}
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {Object.entries(groupedLabor).map(([category, items]) => (
            <div key={category}>
              <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                <Briefcase className="w-3 h-3" />
                {category}
              </p>
              <div className="space-y-1">
                {items.map(labor => (
                  <div 
                    key={labor.id}
                    className="flex items-center justify-between p-2 bg-muted/20 rounded text-sm hover:bg-muted/40"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{labor.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{labor.description}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        <p className="font-bold text-success">${labor.hourlyRate}/hr</p>
                        <p className="text-xs text-muted-foreground">OT: ${labor.overtimeRate}/hr</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(labor)}>
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDelete(labor.id)}>
                          <Trash2 className="w-3 h-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {filteredLabor.length === 0 && (
          <p className="text-center text-muted-foreground py-4">No labor types found</p>
        )}
      </CardContent>
    </Card>
  );
};

// Default equipment types
const defaultEquipmentTypes = [
  // Earthmoving
  { id: 1, name: 'Excavator (Mini)', category: 'Earthmoving', dailyRate: 350, weeklyRate: 1400, monthlyRate: 3500, description: '3-5 ton mini excavator' },
  { id: 2, name: 'Excavator (Standard)', category: 'Earthmoving', dailyRate: 650, weeklyRate: 2600, monthlyRate: 7500, description: '15-20 ton excavator' },
  { id: 3, name: 'Excavator (Large)', category: 'Earthmoving', dailyRate: 1200, weeklyRate: 4800, monthlyRate: 14000, description: '30+ ton excavator' },
  { id: 4, name: 'Backhoe', category: 'Earthmoving', dailyRate: 450, weeklyRate: 1800, monthlyRate: 4500, description: 'Standard backhoe loader' },
  { id: 5, name: 'Skid Steer', category: 'Earthmoving', dailyRate: 275, weeklyRate: 1100, monthlyRate: 2800, description: 'Skid steer loader' },
  { id: 6, name: 'Track Loader', category: 'Earthmoving', dailyRate: 450, weeklyRate: 1800, monthlyRate: 4500, description: 'Compact track loader' },
  { id: 7, name: 'Bulldozer (D6)', category: 'Earthmoving', dailyRate: 850, weeklyRate: 3400, monthlyRate: 9500, description: 'D6 class bulldozer' },
  
  // Trucks
  { id: 8, name: 'Dump Truck (Single Axle)', category: 'Trucks', dailyRate: 350, weeklyRate: 1400, monthlyRate: 3500, description: '5-6 cy dump truck' },
  { id: 9, name: 'Dump Truck (Tandem)', category: 'Trucks', dailyRate: 450, weeklyRate: 1800, monthlyRate: 4500, description: '12-14 cy tandem axle' },
  { id: 10, name: 'Semi End Dump', category: 'Trucks', dailyRate: 550, weeklyRate: 2200, monthlyRate: 5500, description: 'End dump semi trailer' },
  { id: 11, name: 'Water Truck', category: 'Trucks', dailyRate: 400, weeklyRate: 1600, monthlyRate: 4000, description: '2000-4000 gallon water truck' },
  { id: 12, name: 'Flatbed Truck', category: 'Trucks', dailyRate: 300, weeklyRate: 1200, monthlyRate: 3000, description: 'Standard flatbed' },
  
  // Compaction
  { id: 13, name: 'Vibratory Roller', category: 'Compaction', dailyRate: 350, weeklyRate: 1400, monthlyRate: 3500, description: 'Smooth drum roller' },
  { id: 14, name: 'Sheep\'s Foot Roller', category: 'Compaction', dailyRate: 400, weeklyRate: 1600, monthlyRate: 4000, description: 'Padfoot compactor' },
  { id: 15, name: 'Plate Compactor', category: 'Compaction', dailyRate: 75, weeklyRate: 300, monthlyRate: 750, description: 'Walk-behind plate compactor' },
  { id: 16, name: 'Jumping Jack', category: 'Compaction', dailyRate: 65, weeklyRate: 260, monthlyRate: 650, description: 'Rammer compactor' },
  
  // Lifting
  { id: 17, name: 'Crane (25 Ton)', category: 'Lifting', dailyRate: 1500, weeklyRate: 6000, monthlyRate: 18000, description: '25 ton hydraulic crane' },
  { id: 18, name: 'Crane (50 Ton)', category: 'Lifting', dailyRate: 2500, weeklyRate: 10000, monthlyRate: 30000, description: '50 ton hydraulic crane' },
  { id: 19, name: 'Telehandler', category: 'Lifting', dailyRate: 450, weeklyRate: 1800, monthlyRate: 4500, description: '6000-8000 lb telehandler' },
  { id: 20, name: 'Forklift', category: 'Lifting', dailyRate: 200, weeklyRate: 800, monthlyRate: 2000, description: '5000 lb warehouse forklift' },
  { id: 21, name: 'Boom Lift', category: 'Lifting', dailyRate: 350, weeklyRate: 1400, monthlyRate: 3500, description: '60ft articulating boom' },
  { id: 22, name: 'Scissor Lift', category: 'Lifting', dailyRate: 175, weeklyRate: 700, monthlyRate: 1750, description: '26ft scissor lift' },
  
  // Concrete
  { id: 23, name: 'Concrete Mixer Truck', category: 'Concrete', dailyRate: 500, weeklyRate: 2000, monthlyRate: 5000, description: '8-10 cy mixer truck' },
  { id: 24, name: 'Concrete Pump', category: 'Concrete', dailyRate: 1200, weeklyRate: 4800, monthlyRate: 14000, description: '32m boom pump' },
  { id: 25, name: 'Power Trowel', category: 'Concrete', dailyRate: 125, weeklyRate: 500, monthlyRate: 1250, description: '36" ride-on power trowel' },
  { id: 26, name: 'Concrete Saw', category: 'Concrete', dailyRate: 150, weeklyRate: 600, monthlyRate: 1500, description: 'Walk-behind concrete saw' },
  
  // General
  { id: 27, name: 'Generator (Small)', category: 'General', dailyRate: 85, weeklyRate: 340, monthlyRate: 850, description: '6500 watt portable' },
  { id: 28, name: 'Generator (Large)', category: 'General', dailyRate: 250, weeklyRate: 1000, monthlyRate: 2500, description: '20kw towable generator' },
  { id: 29, name: 'Air Compressor', category: 'General', dailyRate: 150, weeklyRate: 600, monthlyRate: 1500, description: '185 CFM tow-behind' },
  { id: 30, name: 'Light Tower', category: 'General', dailyRate: 125, weeklyRate: 500, monthlyRate: 1250, description: '4-light tower' },
];

const EquipmentRates = ({ equipmentTypes, setEquipmentTypes }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    dailyRate: '',
    weeklyRate: '',
    monthlyRate: '',
    description: ''
  });

  // Initialize with defaults if empty
  useEffect(() => {
    if (!equipmentTypes || equipmentTypes.length === 0) {
      setEquipmentTypes(defaultEquipmentTypes);
    }
  }, [equipmentTypes, setEquipmentTypes]);

  const categories = ['all', ...new Set((equipmentTypes || []).map(e => e.category))];
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredEquipment = (equipmentTypes || []).filter(e => 
    selectedCategory === 'all' || e.category === selectedCategory
  );

  const groupedEquipment = filteredEquipment.reduce((acc, e) => {
    if (!acc[e.category]) acc[e.category] = [];
    acc[e.category].push(e);
    return acc;
  }, {});

  const resetForm = () => {
    setFormData({ name: '', category: '', dailyRate: '', weeklyRate: '', monthlyRate: '', description: '' });
    setShowAddForm(false);
    setEditingEquipment(null);
  };

  const handleSave = () => {
    if (!formData.name || !formData.category || !formData.dailyRate) {
      toast.error('Please fill in required fields');
      return;
    }

    const dailyRate = parseFloat(formData.dailyRate);
    const equipmentData = {
      ...formData,
      dailyRate,
      weeklyRate: parseFloat(formData.weeklyRate) || dailyRate * 4,
      monthlyRate: parseFloat(formData.monthlyRate) || dailyRate * 10
    };

    if (editingEquipment) {
      setEquipmentTypes(equipmentTypes.map(e => 
        e.id === editingEquipment.id ? { ...e, ...equipmentData } : e
      ));
      toast.success('Equipment updated');
    } else {
      setEquipmentTypes([...equipmentTypes, { ...equipmentData, id: Date.now() }]);
      toast.success('Equipment added');
    }
    resetForm();
  };

  const handleEdit = (equipment) => {
    setFormData({
      name: equipment.name,
      category: equipment.category,
      dailyRate: equipment.dailyRate.toString(),
      weeklyRate: equipment.weeklyRate?.toString() || '',
      monthlyRate: equipment.monthlyRate?.toString() || '',
      description: equipment.description || ''
    });
    setEditingEquipment(equipment);
    setShowAddForm(true);
  };

  const handleDelete = (id) => {
    setEquipmentTypes(equipmentTypes.filter(e => e.id !== id));
    toast.success('Equipment deleted');
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <HardHat className="w-5 h-5 text-accent" />
            Equipment Rates
          </CardTitle>
          <Button size="sm" onClick={() => setShowAddForm(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
        >
          {categories.map(cat => (
            <option key={cat} value={cat}>
              {cat === 'all' ? 'All Categories' : cat}
            </option>
          ))}
        </select>

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="p-4 bg-muted/30 rounded-lg space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name *</Label>
                <Input
                  placeholder="Equipment name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Category *</Label>
                <Input
                  placeholder="e.g., Earthmoving"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="h-8 text-sm"
                  list="equipCategories"
                />
                <datalist id="equipCategories">
                  {categories.filter(c => c !== 'all').map(cat => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Daily Rate ($) *</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={formData.dailyRate}
                  onChange={(e) => setFormData({ ...formData, dailyRate: e.target.value })}
                  className="h-8 text-sm"
                  min="0"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Weekly Rate ($)</Label>
                <Input
                  type="number"
                  placeholder="Auto: 4x daily"
                  value={formData.weeklyRate}
                  onChange={(e) => setFormData({ ...formData, weeklyRate: e.target.value })}
                  className="h-8 text-sm"
                  min="0"
                />
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Monthly Rate ($)</Label>
                <Input
                  type="number"
                  placeholder="Auto: 10x daily"
                  value={formData.monthlyRate}
                  onChange={(e) => setFormData({ ...formData, monthlyRate: e.target.value })}
                  className="h-8 text-sm"
                  min="0"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Description</Label>
              <Input
                placeholder="Brief description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="h-8 text-sm"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={resetForm}>
                <X className="w-3 h-3 mr-1" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave}>
                <Save className="w-3 h-3 mr-1" />
                Save
              </Button>
            </div>
          </div>
        )}

        {/* Equipment List */}
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {Object.entries(groupedEquipment).map(([category, items]) => (
            <div key={category}>
              <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                <HardHat className="w-3 h-3" />
                {category}
              </p>
              <div className="space-y-1">
                {items.map(equip => (
                  <div 
                    key={equip.id}
                    className="flex items-center justify-between p-2 bg-muted/20 rounded text-sm hover:bg-muted/40"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{equip.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{equip.description}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        <p className="font-bold text-accent">${equip.dailyRate}/day</p>
                        <p className="text-xs text-muted-foreground">
                          ${equip.weeklyRate}/wk • ${equip.monthlyRate}/mo
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEdit(equip)}>
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDelete(equip.id)}>
                          <Trash2 className="w-3 h-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {filteredEquipment.length === 0 && (
          <p className="text-center text-muted-foreground py-4">No equipment found</p>
        )}
      </CardContent>
    </Card>
  );
};

export { LaborRates, EquipmentRates, defaultLaborTypes, defaultEquipmentTypes, tabSpecificLaborTypes, tabSpecificEquipmentTypes };
